import React from "react";
import {
  FiArrowUpRight,
  FiCheck,
  FiMonitor,
  FiServer,
  FiGitBranch,
  FiDatabase,
  FiCloud,
  FiUploadCloud,
} from "react-icons/fi";
import { pageTitle } from "../../helpers/PageTitle";

const projects = [
  {
    number: "01",
    category: "FINTECH  •  AI  •  SAAS",
    title: "Intelligent Finance & Reconciliation Platform",
    tagline: "Making complex financial operations simpler.",
    description:
      "A cloud-native financial operations platform that automates high-volume transaction processing, reconciliation, receivables, and exception management across the stack.",
    image: "/images/others/details_analytics_1.jpeg",
    imageAlt: "Team working on a financial analytics platform",
    accent: "#1b667a",
    built: [
      "Real-time finance dashboards",
      "Automated payment reconciliation",
      "Invoice and receivables management",
      "Intelligent exception handling",
      "Event-driven microservices",
      "High-volume transaction processing",
      "Cloud-native deployment",
    ],
    stack: [
      "React.js",
      "Tailwind CSS",
      "Java",
      "Spring Boot",
      "Python",
      "Kafka",
      "PostgreSQL",
      "AWS",
    ],
  },
  {
    number: "02",
    category: "BANKING  •  ENTERPRISE  •  WORKFLOW AUTOMATION",
    title: "Banking Solution Platform",
    tagline:
      "Powering connected banking journeys with scalable digital architecture.",
    description:
      "A distributed banking platform supporting complex customer and operational journeys through digital interfaces, workflow engines, messaging, and enterprise systems.",
    image: "/images/others/details_api_1.jpeg",
    imageAlt: "Digital architecture represented by code and APIs",
    accent: "#2563EB",
    built: [
      "Customer-facing banking journeys",
      "Application and operational dashboards",
      "Spring Boot microservices",
      "Workflow orchestration",
      "Event-driven communication",
      "Audit and reporting services",
      "Automated customer and internal notifications",
      "AWS cloud deployment and continuous delivery",
    ],
    stack: [
      "Angular",
      "JavaScript",
      "Java",
      "Spring Boot",
      "Camunda",
      "Kafka",
      "PostgreSQL",
      "AWS",
    ],
  },
  {
    number: "03",
    category: "HEALTHCARE  •  AUTOMATION  •  CLOUD",
    title: "Healthcare Workflow Platform",
    tagline:
      "Turning complex healthcare processes into connected digital workflows.",
    description:
      "A healthcare technology platform coordinating clinical and operational processes across users, business rules, backend services, and external systems.",
    image: "/images/others/details_cloud_1.jpeg",
    imageAlt: "Cloud architecture and connected platform planning",
    accent: "#7C3AED",
    built: [
      "Responsive healthcare workflows",
      "Operational dashboards",
      "Backend microservices",
      "Business process automation",
      "Decision automation",
      "Third-party integrations",
      "Cloud infrastructure",
      "Automated deployment",
    ],
    stack: [
      "React.js",
      "JavaScript",
      "Java",
      "Spring Boot",
      "Camunda",
      "BPMN",
      "Docker",
      "Kubernetes",
    ],
  },
  {
    number: "04",
    category: "INSURTECH  •  ENTERPRISE  •  AUTOMATION",
    title: "Insurance Automation Platform",
    tagline: "Building faster, more adaptable insurance journeys.",
    description:
      "A full-stack insurance platform digitizing customer interaction, business validation, workflow execution, and enterprise integrations with independently evolving services.",
    image: "/images/others/details_web_dev_1.jpeg",
    imageAlt: "Product workflow mapped across a digital interface",
    accent: "#D97706",
    built: [
      "Digital insurance journeys",
      "Responsive web experiences",
      "Spring Boot microservices",
      "Workflow automation",
      "Business-rule processing",
      "Enterprise integrations",
      "Cloud-native infrastructure",
      "Continuous delivery",
    ],
    stack: [
      "Angular",
      "JavaScript",
      "Java",
      "Spring Boot",
      "Camunda",
      "REST APIs",
      "SQL",
      "AWS",
    ],
  },
  {
    number: "05",
    category: "FINTECH  •  COMPLIANCE  •  ENTERPRISE",
    title: "Enterprise E-Invoicing Platform",
    tagline: "Simplifying digital invoicing at enterprise scale.",
    description:
      "A full-stack electronic invoicing platform for structured invoices, purchase documents, validation, reporting, and compliance-driven financial operations.",
    image: "/images/others/details_consulting_1.jpeg",
    imageAlt: "Planning and designing an enterprise product experience",
    accent: "#0F766E",
    built: [
      "Invoice management",
      "XML document processing",
      "Purchase-order workflows",
      "Compliance validation",
      "Enterprise dashboards",
      "Reporting and exports",
      "System integrations",
      "Cloud deployment",
    ],
    stack: [
      ".NET",
      "C#",
      "ASP.NET MVC",
      "JavaScript",
      "Bootstrap",
      "SQL Server",
      "XML",
      "AWS",
    ],
  },
];

const layers = [
  {
    title: "Experience",
    icon: FiMonitor,
    detail: "React.js • Angular • JavaScript • Tailwind CSS",
    text: "Interfaces designed to be fast, responsive, and intuitive.",
  },
  {
    title: "Platform",
    icon: FiServer,
    detail: "Java • Spring Boot • .NET • Python • REST APIs",
    text: "Backend services designed around clear business domains.",
  },
  {
    title: "Architecture",
    icon: FiGitBranch,
    detail: "Microservices • Distributed Systems • Event-Driven Architecture",
    text: "Systems engineered for scalability, resilience, and independent evolution.",
  },
  {
    title: "Data & Messaging",
    icon: FiDatabase,
    detail: "PostgreSQL • MySQL • SQL Server • Kafka • RabbitMQ",
    text: "Reliable foundations for transactional and event-driven workloads.",
  },
  {
    title: "Cloud",
    icon: FiCloud,
    detail: "AWS • Docker • Kubernetes • CI/CD • Automated Deployment",
    text: "Cloud-native infrastructure built for production workloads.",
  },
  {
    title: "Delivery",
    icon: FiUploadCloud,
    detail: "CI/CD â€¢ Automated Deployment â€¢ Monitoring",
    text: "Reliable, repeatable delivery from commit to production.",
  },
];

function ProjectCard({ project, index }) {
  const reversed = index % 2 !== 0;
  return (
    <article className="group grid overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_12px_45px_rgba(15,23,42,0.055)] lg:grid-cols-2">
      <div
        className={`relative min-h-[300px] overflow-hidden lg:min-h-[430px] ${reversed ? "lg:order-2" : ""}`}
      >
        <img
          src={project.image}
          alt={project.imageAlt}
          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/60 via-transparent to-transparent" />
        <div className="absolute left-6 top-6 flex items-center gap-3 rounded-full border border-white/20 bg-slate-950/25 px-3 py-1.5 text-xs font-bold tracking-[2px] text-white backdrop-blur-md">
          <span style={{ color: project.accent }}>{project.number}</span>{" "}
          SELECTED WORK
        </div>
        <div className="absolute bottom-6 left-6 right-6 text-white">
          <div className="mb-2 text-xs font-bold uppercase tracking-[2px] text-white/65">
            {project.category}
          </div>
          <div
            className="h-1 w-12 rounded-full"
            style={{ backgroundColor: project.accent }}
          />
        </div>
      </div>
      <div
        className={`flex flex-col justify-center p-7 sm:p-9 lg:p-11 ${reversed ? "lg:order-1" : ""}`}
      >
        <p
          className="mb-3 text-xs font-bold uppercase tracking-[2.5px]"
          style={{ color: project.accent }}
        >
          {project.category}
        </p>
        <h2 className="mb-3 text-[28px] font-bold leading-[1.15] tracking-[-0.025em] text-slate-900 sm:text-[32px]">
          {project.title}
        </h2>
        <p className="mb-4 text-base font-semibold leading-7 text-slate-700">
          {project.tagline}
        </p>
        <p className="mb-6 text-[15px] leading-7 text-slate-500">
          {project.description}
        </p>
        <div className="mb-6 grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {project.built.map((item) => (
            <div
              key={item}
              className="flex items-start gap-2 text-sm leading-6 text-slate-600"
            >
              <FiCheck
                className="mt-0.5 shrink-0"
                style={{ color: project.accent }}
              />
              {item}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-5">
          {project.stack.map((item) => (
            <span
              key={item}
              className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-500"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function PortfolioPage() {
  pageTitle("Portfolio");
  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      <div className="pointer-events-none absolute left-[-180px] top-[230px] h-[460px] w-[460px] rounded-full bg-teal-100/35 blur-3xl" />
      <div className="pointer-events-none absolute right-[-180px] top-[900px] h-[520px] w-[520px] rounded-full bg-emerald-100/35 blur-3xl" />
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50/60 px-4 pb-20 pt-[145px] text-center sm:px-6 max-[991px]:pb-14 max-[991px]:pt-[115px]">
        <div
          className="pointer-events-none absolute left-[7%] top-[35%] h-28 w-40 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(#1b667a 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />
        <div
          className="pointer-events-none absolute right-[7%] top-[38%] h-24 w-36 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(#F5A623 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />
        <div className="relative mx-auto max-w-[900px]">
          <div className="mb-5 inline-flex items-center gap-3">
            <span className="h-[2px] w-10 bg-gradient-to-r from-transparent to-[#1b667a]" />
            <span className="text-xs font-bold uppercase tracking-[3px] text-[#1b667a]">
              Selected Work
            </span>
            <span className="h-[2px] w-10 bg-gradient-to-l from-transparent to-[#1b667a]" />
          </div>
          <h1 className="text-[clamp(38px,5vw,62px)] font-semibold leading-[1.05] tracking-[-0.045em] text-[#101828]">
            Technology built
            <br />
            to make an{" "}
            <span className="bg-gradient-to-r from-[#075E68] via-[#0E9F7A] to-[#10B981] bg-clip-text text-transparent">
              impact.
            </span>
          </h1>
          <div className="mx-auto mt-7 h-1 w-12 rounded-full bg-[#1b667a]" />
          <p className="mx-auto mt-7 max-w-[650px] text-[17px] leading-[1.85] text-slate-500">
            We design and engineer digital products across the entire technology
            stack — from intuitive user experiences to distributed backend
            systems and cloud infrastructure.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-2 text-xs font-semibold uppercase tracking-[1.5px] text-slate-400">
            <span>Frontend</span>
            <span className="text-[#F5A623]">•</span>
            <span>Backend</span>
            <span className="text-[#F5A623]">•</span>
            <span>AI</span>
            <span className="text-[#F5A623]">•</span>
            <span>Microservices</span>
            <span className="text-[#F5A623]">•</span>
            <span>Cloud</span>
            <span className="text-[#F5A623]">•</span>
            <span>Production</span>
          </div>
        </div>
      </section>
      <section className="relative z-10 mx-auto max-w-[1200px] space-y-7 px-4 pb-24 sm:px-6 lg:pb-28">
        {projects.map((project, index) => (
          <ProjectCard key={project.title} project={project} index={index} />
        ))}
      </section>

      <section className="relative z-10 overflow-hidden bg-[#f7fbfa] px-4 py-20 sm:px-6 lg:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="mx-auto mb-12 max-w-[720px] text-center">
            <div className="mb-4 inline-flex items-center gap-3"><span className="h-[2px] w-10 bg-[#1b667a]" /><span className="text-xs font-bold uppercase tracking-[3px] text-[#1b667a]">One engineering partner</span><span className="h-[2px] w-10 bg-[#F5A623]" /></div>
            <h2 className="text-[clamp(32px,4vw,50px)] font-semibold leading-[1.1] tracking-[-0.04em] text-slate-900">Every layer, connected.</h2>
            <p className="mx-auto mt-5 max-w-[620px] text-base leading-7 text-slate-500">Modern products do not stop at the frontend — and neither do we. We connect experience, platform, architecture, data, cloud, and delivery.</p>
          </div>
          <div className="mb-10 flex flex-wrap items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[1.5px] text-slate-400 sm:gap-3"><span>Experience</span><span className="text-[#F5A623]">→</span><span>APIs</span><span className="text-[#F5A623]">→</span><span>Microservices</span><span className="text-[#F5A623]">→</span><span>Data</span><span className="text-[#F5A623]">→</span><span>Cloud</span><span className="text-[#F5A623]">→</span><span>Production</span></div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{layers.map((layer, index) => { const accents = ["#1b667a", "#2563EB", "#7C3AED", "#D97706", "#0F766E", "#475569"]; return <div key={layer.title} className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-7 shadow-[0_2px_16px_rgba(0,0,0,0.05)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(13,79,79,0.16)]"><div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accents[index] }} /><div className="mb-6 flex items-center justify-between"><div className="flex h-14 w-14 items-center justify-center rounded-2xl text-2xl shadow-sm" style={{ backgroundColor: `${accents[index]}14`, color: accents[index] }}><layer.icon size={24} /></div><span className="text-sm font-bold tracking-[2px]" style={{ color: accents[index] }}>0{index + 1}</span></div><h3 className="mb-3 text-[22px] font-bold tracking-[-0.01em] text-gray-900">{layer.title}</h3><p className="mb-4 min-h-[52px] text-base font-semibold leading-6 text-gray-600">{layer.detail}</p><p className="text-sm leading-6 text-gray-400">{layer.text}</p><div className="absolute bottom-0 left-0 h-[3px] w-0 transition-all duration-500 group-hover:w-full" style={{ backgroundColor: accents[index] }} /></div>; })}</div>
        </div>
      </section>

      <section className="relative z-10 px-4 py-20 sm:px-6 lg:py-24">
        <div className="relative mx-auto max-w-[1000px] overflow-hidden rounded-[24px] border border-slate-200 bg-white px-7 py-16 text-center shadow-[0_20px_60px_rgba(15,23,42,0.1)] sm:px-16">
          <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full border border-[#1b667a]/15" />
          <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full border border-[#F5A623]/20" />
          <div className="relative">
            <p className="mb-4 text-xs font-bold uppercase tracking-[3px] text-[#1b667a]">
              From first pixel to production
            </p>
            <h2 className="text-[clamp(30px,4vw,46px)] font-semibold leading-[1.1] tracking-[-0.035em] text-slate-900">
              Have something ambitious to build?
            </h2>
            <p className="mx-auto mt-5 max-w-[570px] text-base leading-7 text-slate-500">
              Whether you are launching a new product, modernizing an existing
              platform, or solving a complex engineering problem, we are ready
              to help.
            </p>
            <a
              href="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-[12px] bg-[#1b667a] px-7 py-3.5 text-[15px] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#155568]"
            >
              Let&apos;s Build What&apos;s Next <FiArrowUpRight size={17} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
